class FyreSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedOnce) return;
    this.isConnectedOnce = true;

    this.slides = Array.from(this.querySelectorAll('[data-slideshow-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));
    this.viewport = this.querySelector('[data-slideshow-viewport]');
    this.previousButton = this.querySelector('[data-slideshow-previous]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.currentOutput = this.querySelector('[data-slideshow-current]');
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.pointerStartX = null;
    this.pointerStartY = null;
    this.isVisible = true;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.slides.length) return;

    this.boundPrevious = () => this.goTo(this.currentIndex - 1, true);
    this.boundNext = () => this.goTo(this.currentIndex + 1, true);
    this.boundKeydown = (event) => this.onKeydown(event);
    this.boundPointerDown = (event) => this.onPointerDown(event);
    this.boundPointerUp = (event) => this.onPointerUp(event);
    this.boundPause = () => this.stopAutoplay();
    this.boundResume = () => this.startAutoplay();
    this.boundVisibility = () => document.hidden ? this.stopAutoplay() : this.startAutoplay();

    this.previousButton?.addEventListener('click', this.boundPrevious);
    this.nextButton?.addEventListener('click', this.boundNext);
    this.addEventListener('keydown', this.boundKeydown);
    this.viewport?.addEventListener('pointerdown', this.boundPointerDown);
    this.viewport?.addEventListener('pointerup', this.boundPointerUp);
    this.addEventListener('mouseenter', this.boundPause);
    this.addEventListener('mouseleave', this.boundResume);
    this.addEventListener('focusin', this.boundPause);
    this.addEventListener('focusout', this.boundResume);
    document.addEventListener('visibilitychange', this.boundVisibility);

    this.slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (index !== this.currentIndex) this.goTo(index, true);
      });
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index, true));
    });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting && entry.intersectionRatio >= .2;
        if (this.isVisible) this.startAutoplay();
        else this.stopAutoplay();
      }, { threshold: [0, .2] });
      this.observer.observe(this);
    }

    this.render(false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.observer?.disconnect();
    this.previousButton?.removeEventListener('click', this.boundPrevious);
    this.nextButton?.removeEventListener('click', this.boundNext);
    this.removeEventListener('keydown', this.boundKeydown);
    this.viewport?.removeEventListener('pointerdown', this.boundPointerDown);
    this.viewport?.removeEventListener('pointerup', this.boundPointerUp);
    this.removeEventListener('mouseenter', this.boundPause);
    this.removeEventListener('mouseleave', this.boundResume);
    this.removeEventListener('focusin', this.boundPause);
    this.removeEventListener('focusout', this.boundResume);
    document.removeEventListener('visibilitychange', this.boundVisibility);
  }

  goTo(index, userInitiated = false) {
    const count = this.slides.length;
    if (count < 2) return;
    this.currentIndex = (index + count) % count;
    this.render(true);
    if (userInitiated) this.restartAutoplay();
  }

  render(announce) {
    const count = this.slides.length;

    this.slides.forEach((slide, index) => {
      let distance = (index - this.currentIndex + count) % count;
      if (distance > count / 2) distance -= count;
      let position = 'hidden';
      if (distance === 0) position = '0';
      else if (distance === -1) position = '-1';
      else if (distance === 1) position = '1';

      slide.dataset.position = position;
      slide.setAttribute('aria-current', distance === 0 ? 'true' : 'false');
      slide.tabIndex = distance === 0 ? 0 : -1;
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (this.currentOutput) {
      if (!announce) this.currentOutput.setAttribute('aria-hidden', 'true');
      this.currentOutput.textContent = String(this.currentIndex + 1);
      if (!announce) requestAnimationFrame(() => this.currentOutput.removeAttribute('aria-hidden'));
    }
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.currentIndex - 1, true);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.currentIndex + 1, true);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0, true);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.slides.length - 1, true);
    }
  }

  onPointerDown(event) {
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
  }

  onPointerUp(event) {
    if (this.pointerStartX === null || this.pointerStartY === null) return;
    const distanceX = event.clientX - this.pointerStartX;
    const distanceY = event.clientY - this.pointerStartY;
    this.pointerStartX = null;
    this.pointerStartY = null;

    if (Math.abs(distanceX) < 42 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
    this.goTo(this.currentIndex + (distanceX < 0 ? 1 : -1), true);
  }

  shouldAutoplay() {
    return this.dataset.autoplay === 'true'
      && this.slides.length > 1
      && this.isVisible
      && !document.hidden
      && !this.prefersReducedMotion.matches;
  }

  startAutoplay() {
    if (!this.shouldAutoplay() || this.autoplayTimer) return;
    const interval = Math.max(Number(this.dataset.interval) || 6000, 2500);
    this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1), interval);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('fyre-slideshow')) {
  customElements.define('fyre-slideshow', FyreSlideshow);
}
